import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { formatCurrency, formatNumberWithDecimal, getCurrentFinancialYear } from "../../../utils/commonUtil";
import { REIButton } from "../../../components/ui";
import { Row } from "primereact/row";
import { ColumnGroup } from "primereact/columngroup";
import { useNavigate } from "react-router-dom";
import { useFilterSelection } from "../FilterSelectionContext";

interface IPropertyCashFlowSummary {
  street: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
  type: string;
  preTaxCashflow: number;
  taxableProfitLoss: number;
  yeildPurchasePrice: number;
  yeildCurrentValue: number;
}

interface IPropertySummary {
  type: string;
  preTaxCashflow: number;
  taxableProfitLoss: number;
  yeildPurchasePrice: number;
  yeildCurrentValue: number;
}

const MyCashFlowSummary: React.FC<{ cashFlowData: any; isForecastOnly: boolean; year: string }> = ({
  cashFlowData,
  isForecastOnly,
  year,
}) => {
  const currentYear = "20" + getCurrentFinancialYear();
  const [transformedData, setTransformedData] = useState<IPropertyCashFlowSummary[]>([]);
  const [transformedFooter, setTransformedFooter] = useState<IPropertySummary[]>([]);
  const [collapsedRows, setCollapsedRows] = useState<Record<string, boolean>>({});
  const { isShowingTotals } = useFilterSelection();
  const navigate = useNavigate();

  useEffect(() => {
    if (cashFlowData && Array.isArray(cashFlowData.PropertyDetails) && cashFlowData.PropertyDetails.length > 0) {
      let groupCounter: number = -1;
      let lastGroupKey: string = "";
      const transformed = cashFlowData.PropertyDetails.map((properties: any) => {
        const groupKey = properties.PropertyDetail.StreetAddress;
        if (groupKey !== lastGroupKey) {
          groupCounter++;
          lastGroupKey = groupKey;
        }
        const actual = !isShowingTotals
          ? properties.Details.CashflowSummaryPortions?.Actuals?.find((item: any) => item.Year === year) || {}
          : properties.Details.CashflowSummaryTotal?.Actuals?.find((item: any) => item.Year === year) || {};
        const forecast = !isShowingTotals
          ? properties.Details.CashflowSummaryPortions?.Forecast?.find((item: any) => item.Year === year) || {}
          : properties.Details.CashflowSummaryTotal?.Forecast?.find((item: any) => item.Year === year) || {};
        const variance = !isShowingTotals
          ? properties.Details.CashflowSummaryPortions?.Variance?.find((item: any) => item.Year === year) || {}
          : properties.Details.CashflowSummaryTotal?.Variance?.find((item: any) => item.Year === year) || {};

        return [
          { type: "Forecast", data: forecast },
          { type: "Actuals", data: actual },
          { type: "Variance", data: variance },
        ].map(({ type, data }) => ({
          street: properties.PropertyDetail?.StreetAddress || "",
          suburb: properties.PropertyDetail?.Suburb || "",
          state: properties.PropertyDetail?.State || "",
          postcode: properties.PropertyDetail?.Postcode || "",
          country: properties.PropertyDetail?.Country || "",
          type,
          preTaxCashflow: Number(data.PreTaxCashflow) || 0,
          taxableProfitLoss: Number(data.NetProfit) || 0,
          yeildPurchasePrice: Number(data.YieldVsPurchase) || 0,
          yeildCurrentValue: Number(data.YieldVsValuation) || 0,
          propertyId: properties.PropertyDetail?.PortfolioTrackerId,
          groupIndex: groupCounter % 2,
        }));
      }).flat();

      setTransformedData(transformed);

      const footerProperties = cashFlowData.TotalForDetails.find((item: any) => item.EntityId === "") || {};
      const actualFooter = isShowingTotals
        ? footerProperties.CashflowDetailsTotals.Summary?.Actuals?.find((item: any) => item.Year === year) || {}
        : footerProperties.CashflowDetailsPortions.Summary?.Actuals?.find((item: any) => item.Year === year) || {};
      const forecastFooter = isShowingTotals
        ? footerProperties.CashflowDetailsTotals.Summary?.Forecast?.find((item: any) => item.Year === year) || {}
        : footerProperties.CashflowDetailsPortions.Summary?.Forecast?.find((item: any) => item.Year === year) || {};
      const varianceFooter = isShowingTotals
        ? footerProperties.CashflowDetailsTotals.Summary?.Variance?.find((item: any) => item.Year === year) || {}
        : footerProperties.CashflowDetailsPortions.Summary?.Variance?.find((item: any) => item.Year === year) || {};

      const footer = [
        {
          type: "Actuals",
          preTaxCashflow: Number(actualFooter.PreTaxCashflow) || 0,
          taxableProfitLoss: Number(actualFooter.NetProfit) || 0,
          yeildPurchasePrice: Number(actualFooter.YieldVsPurchase) || 0,
          yeildCurrentValue: Number(actualFooter.YieldVsValuation) || 0,
        },
        {
          type: "Forecast",
          preTaxCashflow: Number(forecastFooter.PreTaxCashflow) || 0,
          taxableProfitLoss: Number(forecastFooter.NetProfit) || 0,
          yeildPurchasePrice: Number(forecastFooter.YieldVsPurchase) || 0,
          yeildCurrentValue: Number(forecastFooter.YieldVsValuation) || 0,
        },
        {
          type: "Variance",
          preTaxCashflow: Number(varianceFooter.PreTaxCashflow) || 0,
          taxableProfitLoss: Number(varianceFooter.NetProfit) || 0,
          yeildPurchasePrice: Number(varianceFooter.YieldVsPurchase) || 0,
          yeildCurrentValue: Number(varianceFooter.YieldVsValuation) || 0,
        },
      ];

      setTransformedFooter(footer);
    }
  }, [cashFlowData, year, isShowingTotals]);

  const currencyTemplate = (value: number) => (
    <span className={value < 0 ? "text-red-600" : ""}>
      {value < 0 ? `(${formatCurrency(-value)})` : formatCurrency(value)}
    </span>
  );

  const percentageTemplate = (value: number) => (
    <span className={value < 0 ? "text-red-600" : ""}>
      {value < 0 ? `(${formatNumberWithDecimal(-value, 2, 2)}%)` : `${formatNumberWithDecimal(value, 2, 2)}%`}
    </span>
  );

  const renderCell = (rowData: any, field: string, templateFn: (value: number) => JSX.Element) => {
    const shouldRender = isForecastOnly
      ? collapsedRows[rowData.street] || rowData.type === "Forecast"
      : !collapsedRows[rowData.street] || rowData.type === "Forecast";

    if (
      Number(year) > Number(currentYear) &&
      rowData.type !== "Forecast" &&
      rowData[field] !== "Actuals" &&
      rowData[field] !== "Variance"
    ) {
      return null;
    }
    return shouldRender ? templateFn(rowData[field]) : null;
  };

  const toggleRowCollapse = (street: string) => {
    setCollapsedRows((prev) => ({ ...prev, [street]: !prev[street] }));
  };

  const renderFooter = (category: any, data: IPropertySummary) =>
    transformedFooter && (
      <Row>
        <Column footer={category == "Forecast" ? "Totals/averages" : ""} />
        <Column field="type" footer={category} />
        <Column footer={currencyTemplate(data?.preTaxCashflow)} footerStyle={{ textAlign: "right" }} />
        <Column footer={currencyTemplate(data?.taxableProfitLoss)} footerStyle={{ textAlign: "right" }} />
        <Column footer={percentageTemplate(data?.yeildPurchasePrice)} footerStyle={{ textAlign: "right" }} />
        <Column footer={percentageTemplate(data?.yeildCurrentValue)} footerStyle={{ textAlign: "right" }} />{" "}
      </Row>
    );

  const footerGroup =
    transformedFooter &&
    (Number(year) > Number(currentYear) ? (
      <ColumnGroup>
        {renderFooter("Forecast", transformedFooter.find((x) => x.type == "Forecast")!)}
        {!isForecastOnly && (
          <Row>
            <Column footer={""} />
            <Column footer={"Actual"} />
            <Column footer={""} />
            <Column footer={""} />
            <Column footer={""} />
            <Column footer={""} />
          </Row>
        )}
        {!isForecastOnly && (
          <Row>
            <Column footer={""} />
            <Column footer={"Variance"} />
            <Column footer={""} />
            <Column footer={""} />
            <Column footer={""} />
            <Column footer={""} />
          </Row>
        )}
      </ColumnGroup>
    ) : (
      <ColumnGroup>
        {renderFooter("Forecast", transformedFooter.find((x) => x.type == "Forecast")!)}
        {!isForecastOnly && renderFooter("Actuals", transformedFooter.find((x) => x.type == "Actuals")!)}
        {!isForecastOnly && renderFooter("Variance", transformedFooter.find((x) => x.type == "Variance")!)}
      </ColumnGroup>
    ));

  return (
    transformedData &&
    transformedFooter && (
      <div>
        <DataTable
          value={transformedData}
          rowGroupMode="rowspan"
          groupRowsBy="street"
          size="small"
          footerColumnGroup={footerGroup}
          rowClassName={(rowData) => (rowData.groupIndex === 1 ? "groupRowGrey" : "")}
        >
          <Column
            field="street"
            header="Address/property name"
            body={(rowData) => (
              <div className="flex items-center">
                {isForecastOnly && (
                  <REIButton
                    id={`mcfsum-btn-collapse-${rowData.propertyId}`}
                    icon={collapsedRows[rowData.street] ? "pi pi-angle-down" : "pi pi-angle-right"}
                    text={true}
                    onClick={() => toggleRowCollapse(rowData.street)}
                  />
                )}
                {!isForecastOnly && (
                  <REIButton
                    id={`mcfsum-btn-collapse-${rowData.propertyId}`}
                    icon={collapsedRows[rowData.street] ? "pi pi-angle-right" : "pi pi-angle-down"}
                    text={true}
                    onClick={() => toggleRowCollapse(rowData.street)}
                  />
                )}
                <a
                  id={`mcfsum-lnk-address-${rowData.propertyId}`}
                  data-testid={`mcfsum-lnk-address-${rowData.propertyId}`}
                  className="cursor-pointer text-blue-700"
                  onClick={() => {
                    navigate(`/portfolio-tracker`, {
                      state: {
                        tab: "MyPropertyTab",
                        MyPropertyId: rowData.propertyId,
                      },
                    });
                  }}
                >
                  {[rowData.street, rowData.suburb, rowData.state, rowData.postcode].filter(Boolean).join(", ")}
                  {rowData.country ? ` (${rowData.country})` : ""}
                </a>
              </div>
            )}
          />
          <Column field="type" header="Type" body={(rowData) => renderCell(rowData, "type", (value) => value)} />
          <Column
            field="preTaxCashflow"
            header="Pre-tax cash flow"
            body={(rowData) => renderCell(rowData, "preTaxCashflow", currencyTemplate)}
            align="right"
          />
          <Column
            field="taxableProfitLoss"
            header="Taxable profit/loss"
            body={(rowData) => renderCell(rowData, "taxableProfitLoss", currencyTemplate)}
            align="right"
          />
          <Column
            field="yeildPurchasePrice"
            header="Yield % of purchase price"
            body={(rowData) => renderCell(rowData, "yeildPurchasePrice", percentageTemplate)}
            align="right"
          />
          <Column
            field="yeildCurrentValue"
            header="Yield % of current value"
            body={(rowData) => renderCell(rowData, "yeildCurrentValue", percentageTemplate)}
            align="right"
          />
        </DataTable>
      </div>
    )
  );
};

export default MyCashFlowSummary;
